/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import PlayerSuspensionsList from 'src/components/PlayerSuspensionsList'
import { paragraphMedium } from 'src/components/css'
import StatsTab from '../components/tabs/StatsTab'
import HighlightsTab from '../components/tabs/HighlightsTab'
// import AchievementsTab from '../components/tabs/AchievementsTab'
import OverviewTab from '../components/tabs/OverviewTab'
import { filterBySelectedPlayer } from '@sportninja/common/features/profile/utils/filterBySelectedPlayer'
import { sortByDate } from '../helpers/profileFormatting'
import { ProfilePageConfig, ProfileSuspension } from '../types'

interface UseProfilePagesParams {
  selectedPlayerId: string | null
  suspensionLoading: boolean
  suspensions: ProfileSuspension[] | undefined
}

// Player-scoped tabs render this when no player is selected (e.g. a user with
// no players). Keeps the route mounted instead of a blank screen.
const NoPlayerSelected = () => (
  <div
    css={css`
      ${paragraphMedium}
      color: ${colors.NEUTRAL_50};
      text-align: center;
      padding: 48px;
    `}
  >
    Select a player to view this tab.
  </div>
)

export const useProfilePages = ({
  selectedPlayerId,
  suspensionLoading,
  suspensions,
}: UseProfilePagesParams): ProfilePageConfig[] => {
  // Player-scoped tabs are only constructed with a real (non-null) player id;
  // when no player is selected they render the empty state instead. This keeps
  // `playerId` honestly typed as `string` (no casts) and prevents the reused
  // PlayerProvider from firing a request for a null id.
  const scoped = (render: (playerId: string) => React.ReactNode) =>
    selectedPlayerId ? render(selectedPlayerId) : <NoPlayerSelected />

  return [
    {
      text: capitalizeWords('Overview'),
      to: ROUTES.PROFILE.ROOT,
      component: scoped((playerId) => <OverviewTab playerId={playerId} />),
    },
    {
      text: capitalizeWords('Stats'),
      to: ROUTES.PROFILE.STATS,
      component: scoped((playerId) => <StatsTab playerId={playerId} />),
    },
    {
      text: capitalizeWords('Suspensions'),
      to: ROUTES.PROFILE.SUSPENSIONS,
      component: (
        <PlayerSuspensionsList
          isProfileView
          loading={suspensionLoading}
          error={null}
          suspensions={sortByDate(
            filterBySelectedPlayer(suspensions ?? [], selectedPlayerId)
          )}
        />
      ),
    },
    // {
    //   text: capitalizeWords('Achievements'),
    //   to: ROUTES.PROFILE.ACHIEVEMENTS,
    //   component: <AchievementsTab />,
    // },
    {
      text: capitalizeWords('Highlights'),
      to: ROUTES.PROFILE.HIGHLIGHTS,
      component: scoped((playerId) => <HighlightsTab playerId={playerId} />),
    },
  ]
}
